import axios from 'axios'
import {
  DeliverynoteDraftInterface,
  DeliverynoteInterface,
} from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { ReceiptDraftInterface } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'

import { Analytics, checkPlacement } from '../Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from '../Global/AnalyticsEvents'
import { DocumentType, DocumentTypes, DraftInterface } from '../Interfaces/Draft/DraftInterface'
import { getRequestConfig } from './index'

type DraftInterfaceV1 = ReceiptDraftInterface | DeliverynoteDraftInterface

export async function getDraft(type: DocumentType, id: number): Promise<DraftInterface> {
  const response = await axios.get(`/rest-invoice/api/drafts/${type}s/${id}`, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function updateDraft(type: DocumentType, draft: DraftInterface): Promise<DraftInterface> {
  Analytics.trackEvent(Events.editDocument, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: checkPlacement(window.location.pathname),
    documentType: type,
    draftId: draft.id,
  })
  const response = await axios.put(`/rest-invoice/api/drafts/${type}s/${draft.id}`, draft, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function createDraft(type: DocumentType, draft: DraftInterface, invoiceCount?: number): Promise<number> {
  Analytics.trackEvent(Events.saveDraft, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: checkPlacement(window.location.pathname),
    documentType: type,
    firstDocument: invoiceCount === 0,
    documentCount: invoiceCount,
  })

  if (type === 'invoice') {
    Segment.track(SegmentEvents.draft.add)
  }

  const response = await axios.post(`/rest-invoice/api/drafts/${type}s`, draft, getRequestConfig())
  const createdId = parseInt(response.headers.location.split('/').slice(-1), 10)
  return Promise.resolve(createdId)
}

export async function approveNonInvoiceDraft(
  type: DocumentType,
  draft: DraftInterface,
  estimateCount?: number,
): Promise<{ id: number }> {
  const response = await axios.put(`/rest-invoice/api/drafts/${type}s/${draft.id}/approve`, null, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function approveInvoiceDraft(draft: DraftInterface, invoiceCount?: number): Promise<[{ id: number }]> {
  Analytics.trackEvent(`${Events.approveDocument}`, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: Placement.preview,
    documentType: 'invoice',
    firstDocument: invoiceCount === 0,
    documentCount: invoiceCount,
  })

  Segment.track(SegmentEvents.invoice.add, { invoice_lines: draft?.lines?.length || 0 })

  const response = await axios.put(`/rest-invoice/api/drafts/invoices/${draft.id}/approve`, null, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function deleteDraft(type: DocumentType, id: number) {
  Analytics.trackEvent(Events.deleteDraft, {
    type: AnalyticsType.action,
    category: Category.draft,
    result: Result.success,
    placement: checkPlacement(window.location.pathname),
    documentType: type,
  })
  const response = await axios.delete(`/rest-invoice/api/drafts/${type}s/${id}`, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function deleteDraftV1(type: DocumentType, id: number) {
  const response = await axios.delete(`/rest-invoice/api/v1/drafts/${type}s/${id}`, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function getHtmlPreview(type: DocumentType, draft: DraftInterface): Promise<string[]> {
  const response = await axios.post(`/rest-invoice/api/drafts/${type}s/preview/html`, draft, getRequestConfig())
  const data = type === DocumentTypes.INVOICE ? response.data : [response.data]
  return Promise.resolve(data)
}

export async function getHtmlPreviewV1(type: DocumentType, draft: DraftInterfaceV1): Promise<string[]> {
  const response = await axios.post(`/rest-invoice/api/v1/drafts/${type}s/preview/html`, draft, getRequestConfig())
  const data = type === DocumentTypes.INVOICE ? response.data : [response.data]
  return Promise.resolve(data)
}

export async function createDraftV1(type: DocumentType, draft: DraftInterfaceV1): Promise<DraftInterfaceV1> {
  const response = await axios.post(`/rest-invoice/api/v1/drafts/${type}s`, draft, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function getDraftV1(type: DocumentType, id: number): Promise<DraftInterfaceV1> {
  const response = await axios.get(`/rest-invoice/api/v1/drafts/${type}s/${id}`, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function updateDraftV1(type: DocumentType, draft: DraftInterfaceV1): Promise<DraftInterfaceV1> {
  const response = await axios.put(`/rest-invoice/api/v1/drafts/${type}s/${draft.id}`, draft, getRequestConfig())
  return Promise.resolve(response.data)
}

export async function approveDraftV1(type: DocumentType, id: DraftInterfaceV1['id']): Promise<DeliverynoteInterface> {
  const response = await axios.post(`/rest-invoice/api/v1/drafts/${type}s/${id}/approve`, null, getRequestConfig())
  return Promise.resolve(response.data)
}
